/**
 * Normalise a CSS color to 0–1 RGBA for Lottie slot binding.
 *
 * The trap this exists to dodge: `getComputedStyle(el).color` serialises a
 * wide-gamut color (this app's `--color-accent` is `light-dark(oklch(…),
 * oklch(…))`) back as `"oklch(…)"`, NOT `"rgb(…)"`. A regex that only reads
 * `rgb()/rgba()` returns null for every theme-token accent, so the slot silently
 * falls back to the document's baked default. Canvas 2D `fillStyle` accepts the
 * full CSS Color 4 syntax (oklch/color()/named/hex), so we rasterise as a
 * fallback and read the sRGB pixel back.
 */

/** Parse an `rgb()/rgba()` string → 0–1 rgba, or null if it isn't that form. */
export function rgbStringToRgba(s: string): [number, number, number, number] | null {
  const m = s.match(/rgba?\(([\d.]+)[, ]+([\d.]+)[, ]+([\d.]+)(?:[,/ ]+([\d.]+))?\)/);
  if (!m) return null;
  return [Number(m[1]) / 255, Number(m[2]) / 255, Number(m[3]) / 255, m[4] === undefined ? 1 : Number(m[4])];
}

/**
 * A resolved computed color string → 0–1 rgba. Fast-path parses `rgb()/rgba()`;
 * otherwise `paint` rasterises any CSS color (oklch/color()/named/hex) to 0–255
 * rgba and we scale it. `paint` is injected so the pure dispatch is testable
 * without a real canvas. Returns null when both fail.
 */
export function normalizeComputedColor(
  computed: string,
  paint: (c: string) => [number, number, number, number] | null,
): [number, number, number, number] | null {
  const fast = rgbStringToRgba(computed);
  if (fast) return fast;
  if (!computed) return null;
  const px = paint(computed);
  return px ? [px[0] / 255, px[1] / 255, px[2] / 255, px[3] / 255] : null;
}

/** Rasterise any CSS color via a 1×1 canvas → 0–255 rgba (or null if unsupported). */
export function paintColorToRgba255(color: string): [number, number, number, number] | null {
  try {
    const cv = document.createElement('canvas');
    cv.width = 1;
    cv.height = 1;
    const ctx = cv.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#000';
    ctx.fillStyle = color; // canvas parses the full CSS Color 4 syntax
    ctx.fillRect(0, 0, 1, 1);
    const d = ctx.getImageData(0, 0, 1, 1).data;
    return [d[0], d[1], d[2], d[3]];
  } catch {
    return null;
  }
}
